import React from 'react';
import { motion } from 'framer-motion';
import { 
  HeartIcon,
  BoltIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  GlobeAltIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

const About: React.FC = () => {
  const values = [
    {
      icon: HeartIcon,
      title: 'Customer First',
      description: 'We prioritize our customers\' needs and strive to exceed expectations with every interaction.'
    },
    {
      icon: BoltIcon,
      title: 'Innovation',
      description: 'Continuously adopting the latest technology to provide cutting-edge internet solutions.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Reliability',
      description: 'Committed to delivering consistent, dependable service you can count on 24/7.'
    },
    {
      icon: UserGroupIcon,
      title: 'Community Focus',
      description: 'Deeply invested in the communities we serve and dedicated to their digital growth.'
    }
  ];

  const milestones = [
    {
      year: '2014',
      title: 'Airmax Founded',
      description: 'Started with a mission to bring high-speed internet to underserved Arkansas areas.'
    },
    {
      year: '2020',
      title: 'Network Expansion',
      description: 'Plans to expand Network to Golden Valley Arizona with the community in mind to bring commercial internet speeds to the valley.'
    },
    {
      year: '2022',
      title: 'Network Infrastructure and Planning',
      description: 'Network infrastructure and planning.'
    },
    {
      year: '2023',
      title: 'Service Launch',
      description: 'Started providing services to Golden Valley Arizona.'
    },
    {
      year: '2024',
      title: 'Planning to Expand',
      description: 'Planning to Expand to Kingman area.'
    },
    {
      year: '2026',
      title: 'Kingman Service Launch',
      description: 'Start Providing Services to Kingman Arizona area.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-airmax-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              About Airmax Internet
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Connecting communities with fast, reliable internet service since 2014.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                At Airmax Internet, our mission is to bridge the digital divide in Arizona by providing 
                fast, reliable, and affordable internet access to all communities. We believe that everyone 
                deserves access to high-speed internet, regardless of where they live.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                We're committed to using cutting-edge technology to deliver exceptional service while maintaining 
                the personal touch that only a local provider can offer. Our team lives and works in the communities 
                we serve, ensuring we understand your needs and can respond quickly to any issues.
              </p>
              <div className="text-center">
                <div className="text-3xl font-bold text-airmax-blue mb-2">24/7</div>
                <div className="text-gray-600">Local Support</div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-gray-100 rounded-2xl p-8"
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl text-center">
                  <GlobeAltIcon className="h-12 w-12 text-airmax-blue mx-auto mb-4" />
                  <div className="text-2xl font-bold text-gray-900">2+</div>
                  <div className="text-gray-600">Arizona Communities</div>
                </div>
                <div className="bg-white p-6 rounded-xl text-center">
                  <UserGroupIcon className="h-12 w-12 text-airmax-blue mx-auto mb-4" />
                  <div className="text-2xl font-bold text-gray-900">Local</div>
                  <div className="text-gray-600">Happy Customers</div>
                </div>
                <div className="bg-white p-6 rounded-xl text-center">
                  <BoltIcon className="h-12 w-12 text-airmax-blue mx-auto mb-4" />
                  <div className="text-2xl font-bold text-gray-900">2 Gbps</div>
                  <div className="text-gray-600">Max Speed</div>
                </div>
                <div className="bg-white p-6 rounded-xl text-center">
                  <TrophyIcon className="h-12 w-12 text-airmax-blue mx-auto mb-4" />
                  <div className="text-2xl font-bold text-gray-900">4.9/5</div>
                  <div className="text-gray-600">Customer Rating</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Airmax Internet.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-airmax-blue rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <value.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From a small startup to a leading internet provider in Arizona.
            </p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-300"></div>
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative flex items-center mb-8 ${
                  index % 2 === 0 ? 'justify-start' : 'justify-end'
                }`}
              >
                <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="text-airmax-blue font-bold mb-2">{milestone.year}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-airmax-blue rounded-full border-4 border-white"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the experienced professionals leading Airmax Internet.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="w-44 h-44 rounded-full mx-auto mb-4 overflow-hidden bg-white">
                <img 
                  src={`${process.env.PUBLIC_URL}/images/erick.png?v=${Date.now()}`}
                  alt="Erick Santisteban"
                  className="w-full h-full object-contain"
                  style={{ transform: 'scale(1.3) translateY(15px)' }}
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Erick Santisteban</h3>
              <p className="text-airmax-blue mb-2">CEO & Founder</p>
              <p className="text-gray-600">20+ years in telecommunications, network infrastructure, and advanced technical support leadership.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-44 h-44 rounded-full mx-auto mb-4 overflow-hidden bg-gray-200">
                <img 
                  src={`${process.env.PUBLIC_URL}/images/preston.png`}
                  alt="Preston Santisteban"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 10%' }}
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Preston Santisteban</h3>
              <p className="text-airmax-blue mb-2">VP & Regional Master Technician</p>
              <p className="text-gray-600">8+ years Expert in wireless technology, network architecture, and customer relations.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-airmax-blue to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Experience the Airmax Difference?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join neighbors in Golden Valley and Kingman who trust us for their internet.
            </p>
            <a
              href="tel:928-565-6707"
              className="btn-primary bg-white text-airmax-blue hover:bg-gray-100 text-lg px-8 py-4 inline-flex items-center justify-center"
            >
              Call Us Today: 928-565-6707
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
